<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Switch from 'next/switch/Switch.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const { accountId } = useAccount();
const { t } = useI18n();

const apps = ref([]);
const members = ref([]);
const grantMap = ref({});
const loading = ref(true);
const pending = ref({});
// Espelho, por slug, do `requires_mfa` que o servidor JÁ confirmou. É a fonte
// do v-model do Switch, então só entra aqui valor vindo de resposta.
const mfaState = ref({});
const mfaPending = ref({});

const base = `/api/v1/accounts/${accountId.value}/powerapp_accesses`;
const appsBase = `/api/v1/accounts/${accountId.value}/powerapps`;
const key = (appId, userId) => `${appId}:${userId}`;

const load = async () => {
  try {
    const { data } = await window.axios.get(base);
    apps.value = data.apps;
    const mfa = {};
    data.apps.forEach(a => {
      mfa[a.slug] = !!a.requires_mfa;
    });
    mfaState.value = mfa;
    members.value = data.members;
    const map = {};
    data.grants.forEach(g => {
      map[key(g.account_app_id, g.user_id)] = g.id;
    });
    grantMap.value = map;
  } catch {
    useAlert(t('POWERAPPS_SETTINGS.ERROR'));
  } finally {
    loading.value = false;
  }
};

const isGranted = (appId, userId) => key(appId, userId) in grantMap.value;

const toggle = async (app, member) => {
  const k = key(app.id, member.id);
  if (pending.value[k]) return;
  pending.value = { ...pending.value, [k]: true };
  try {
    if (k in grantMap.value) {
      await window.axios.delete(`${base}/${grantMap.value[k]}`);
      const next = { ...grantMap.value };
      delete next[k];
      grantMap.value = next;
    } else {
      const { data } = await window.axios.post(base, {
        account_app_id: app.id,
        user_id: member.id,
      });
      grantMap.value = { ...grantMap.value, [k]: data.id };
    }
  } catch {
    useAlert(t('POWERAPPS_SETTINGS.ERROR'));
    // Force the checkbox to re-read grantMap so it snaps back to the true state.
    grantMap.value = { ...grantMap.value };
  } finally {
    const next = { ...pending.value };
    delete next[k];
    pending.value = next;
  }
};

// ARMADILHA do Switch (next/switch/Switch.vue:14-15): ele inverte o modelValue e
// SÓ DEPOIS emite `change`, com um valor derivado desse estado — não com o valor
// clicado. Medido: com `v-model` o argumento calha de ser o valor desejado, com
// `:model-value` ele vem invertido. Como o sentido do argumento depende de como
// o pai liga a prop, lemos sempre o v-model (`mfaState`), que o Switch acabou de
// escrever de forma síncrona, e nunca o `$event` — daí `toggleMfa(app)` sem ele.
const toggleMfa = async app => {
  const desired = mfaState.value[app.slug];
  // Persistência PESSIMISTA: desfaz na hora a escrita do v-model, para o
  // interruptor continuar mostrando o estado confirmado enquanto o PATCH voa.
  // Esta tela configura segurança — pintar "MFA ligado" antes da confirmação
  // seria mentira. Mesmo espírito do "snap back" do `toggle` acima.
  mfaState.value = { ...mfaState.value, [app.slug]: !desired };
  if (mfaPending.value[app.slug]) return;
  mfaPending.value = { ...mfaPending.value, [app.slug]: true };
  try {
    // Corpo aninhado de propósito: o controller usa `params.require(:powerapp)`,
    // então um corpo plano voltaria 422.
    const { data } = await window.axios.patch(`${appsBase}/${app.slug}`, {
      powerapp: { requires_mfa: desired },
    });
    // Só o eco do servidor promove o valor novo na tela.
    mfaState.value = { ...mfaState.value, [app.slug]: !!data.requires_mfa };
    useAlert(t('POWERAPPS_SETTINGS.MFA_REQUIRED.API.SUCCESS'));
  } catch {
    // `mfaState` já voltou ao valor anterior lá em cima: o interruptor nunca se mexeu.
    useAlert(t('POWERAPPS_SETTINGS.MFA_REQUIRED.API.ERROR'));
  } finally {
    const next = { ...mfaPending.value };
    delete next[app.slug];
    mfaPending.value = next;
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full gap-6 py-4">
    <div>
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ $t('POWERAPPS_SETTINGS.TITLE') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ $t('POWERAPPS_SETTINGS.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="loading" class="text-sm text-n-slate-11">
      {{ $t('POWERAPPS_SETTINGS.LOADING') }}
    </div>

    <div v-else-if="!apps.length" class="text-sm text-n-slate-11">
      {{ $t('POWERAPPS_SETTINGS.NO_APPS') }}
    </div>

    <div v-else-if="!members.length" class="text-sm text-n-slate-11">
      {{ $t('POWERAPPS_SETTINGS.NO_MEMBERS') }}
    </div>

    <div v-else class="flex flex-col gap-8">
      <section v-for="app in apps" :key="app.id" class="flex flex-col gap-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h2 class="text-base font-medium text-n-slate-12">{{ app.name }}</h2>
          <div class="flex items-center gap-2">
            <span class="text-sm text-n-slate-11">
              {{ $t('POWERAPPS_SETTINGS.MFA_REQUIRED.LABEL') }}
            </span>
            <!-- aria-label porque agora são N interruptores na tela, um por app, e o
                 nome acessível de dentro do Switch é um `sr-only` fixo ("Toggle").
                 Com um só na tela, como no molde MfaRequired.vue, isso não aparece;
                 com N, o leitor de tela anuncia "Toggle" N vezes sem dizer de qual app. -->
            <Switch
              v-model="mfaState[app.slug]"
              :aria-label="`${app.name}: ${$t('POWERAPPS_SETTINGS.MFA_REQUIRED.LABEL')}`"
              @change="toggleMfa(app)"
            />
          </div>
        </div>
        <div class="border rounded-xl border-n-weak divide-y divide-n-weak">
          <label
            v-for="member in members"
            :key="member.id"
            class="flex items-center gap-3 px-4 py-3 cursor-pointer"
          >
            <Checkbox
              :model-value="isGranted(app.id, member.id)"
              :disabled="pending[`${app.id}:${member.id}`]"
              class="shrink-0"
              @change="toggle(app, member)"
            />
            <span class="flex flex-col">
              <span class="text-sm font-medium text-n-slate-12">
                {{ member.name }}
              </span>
              <span class="text-xs text-n-slate-11">{{ member.email }}</span>
            </span>
          </label>
        </div>
      </section>
    </div>
  </div>
</template>
